import React, { useCallback, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import cls from 'clsx';
import { ClassNames } from '@kufox/mui/emotion';
import useTheme from '@kufox/mui/hooks/useTheme';
import MaintenanceNotice, { HEIGHT as MaintenanceNoticeHeight } from '../MaintenanceNotice';
import { getScrollY } from '../../common/tools';

const getSize = (size, isShowMaintenance) => {
  return size + (isShowMaintenance ? MaintenanceNoticeHeight : 0);
};

const useStyles = ({ isShowMaintenance, color }) => {
  const height = getSize(80, isShowMaintenance);
  return {
    headerRoomWrapper: `
      height: ${getSize(80, isShowMaintenance)}px;
      @media screen and (max-width: 768px) {
        height: ${getSize(60, isShowMaintenance)}px;
      }
    `,
    headeroom: `
      height: ${height}px;
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      line-height: 80px;
      z-index: 1000;
      background-color: ${color.base};
      transition: background-color 0.5s ease;
      @media screen and (max-width: 768px) {
        height: ${getSize(60, isShowMaintenance)}px;
        line-height: ${getSize(60, isShowMaintenance)}px;
      }
    `,
    transparent: `
      background-color: transparent !important;
    `,
  };
};

const OPACITY_HEIGHT = 145;

const HeadRoom = ({ children, transparent = false, ...otherProps }) => {
  const { maintenance, pathname, maintainancePath, showMaintenance, onCloseMaintenance } =
    otherProps || {};
  const isShowMaintenance = useMemo(() => {
    if (!showMaintenance) return false;
    if (!pathname || !maintainancePath || !maintenance) return false;
    // 主站 首页/行情/交易/资产界面才需要显示停机公告
    const matchPath = _.find(maintainancePath, ({ path }) => path === pathname);
    if (matchPath) {
      return (
        maintenance.maintenance === true && _.includes(maintenance.pcNoticeLocation, matchPath.code)
      );
    }
    return false;
  }, [pathname, maintenance, maintainancePath, showMaintenance]);

  const theme = useTheme();
  const classes = useStyles({ color: theme.colors, isShowMaintenance });
  const [opacity, setOpacity] = useState(false); // 默认不透明

  const OPACITY_SCROLL_HEIGHT = getSize(OPACITY_HEIGHT, isShowMaintenance);
  const transparentMarginBottom = getSize(80, isShowMaintenance);

  const handleScroll = useCallback(
    _.throttle(() => {
      if (!transparent) {
        setOpacity(false);
      } else if (getScrollY() >= OPACITY_SCROLL_HEIGHT) {
        setOpacity(false);
      } else {
        setOpacity(transparent);
      }
    }, 300),
    [transparent, setOpacity],
  );

  useEffect(() => {
    if (getScrollY() >= OPACITY_SCROLL_HEIGHT) {
      setOpacity(false);
    } else {
      setOpacity(transparent);
    }
  }, [transparent]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const closeShow = useCallback(() => {
    if (typeof onCloseMaintenance === 'function') {
      onCloseMaintenance();
    }
  }, [onCloseMaintenance]);

  return (
    <ClassNames>
      {({ css }) => (
        <div
          className={css(classes.headerRoomWrapper)}
          style={{
            marginBottom: transparent ? `-${transparentMarginBottom}px` : 0,
          }}
        >
          <div
            className={cls(css(classes.headeroom), {
              [css(classes.transparent)]: opacity,
            })}
          >
            {isShowMaintenance && (
              <MaintenanceNotice
                maintenance={maintenance}
                maintainancePath={maintainancePath}
                pathname={pathname}
                closeShow={closeShow}
              />
            )}
            {children}
          </div>
        </div>
      )}
    </ClassNames>
  );
};

export default HeadRoom;
